/**
 * Client-side service callbacks — the browser wiring that connects the injected
 * screen props (`onRegister`, `onLogin`, `onVerify`, ...) to the AVOS HTTP API.
 *
 * Web module boundary: this file imports NO backend runtime. It only speaks to
 * the API over `fetch`, exchanging plain JSON. Shared TYPES may come from
 * `@avos/foundation` (type-only). The API server (see
 * `packages/web/scripts/api.mjs`) translates these HTTP calls into real
 * `@avos/core-api` operations against Postgres.
 */
import type { ErrorEnvelope } from "@avos/foundation";
import type {
  RegistrationResult,
} from "../screens/RegistrationScreen.js";
import type { LoginResult } from "../screens/LoginScreen.js";
import type { VerificationResult } from "../screens/EmailVerificationScreen.js";

/** POST JSON to an API path, returning the parsed JSON body. */
async function postJson<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(path, {
    method: "POST",
    headers: { "content-type": "application/json" },
    credentials: "include",
    body: JSON.stringify(body),
  });
  return (await res.json()) as T;
}

/** POST returning the HTTP status alongside the parsed body (for auth flows). */
async function postWithStatus<T>(path: string, body: unknown): Promise<{ status: number; json: T }> {
  const res = await fetch(path, {
    method: "POST",
    headers: { "content-type": "application/json" },
    credentials: "include",
    body: JSON.stringify(body),
  });
  let json: unknown = {};
  try { json = await res.json(); } catch { /* empty */ }
  return { status: res.status, json: json as T };
}

/** Registration → POST /api/register. */
export async function apiRegister(input: {
  email: string;
  password: string;
}): Promise<RegistrationResult> {
  const data = await postJson<
    { ok: true } | { ok: false; error: ErrorEnvelope }
  >("/api/register", input);
  return data;
}

/**
 * Login → POST /api/auth/login. On success the server sets an HttpOnly session
 * cookie and we redirect into the app. On failure we map the server error code
 * to a {@link LoginResult} discriminant the screen renders as a User_Message.
 */
export async function apiLogin(credentials: {
  email: string;
  password: string;
}): Promise<LoginResult> {
  let status: number;
  let json: { ok?: boolean; user?: { mustChangePassword?: boolean }; error?: { error?: { code?: string } } };
  try {
    ({ status, json } = await postWithStatus<{ ok?: boolean; user?: { mustChangePassword?: boolean }; error?: { error?: { code?: string } } }>(
      "/api/auth/login",
      credentials,
    ));
  } catch {
    // Network failure / unreachable server — surface as a generic server error
    // rather than leaking the raw fetch error or implying bad credentials.
    return "serverError";
  }
  if (status === 200 && json.ok) {
    // First-login temporary-password users must set a new password before the
    // app is usable: route them straight to /change-password. The server also
    // enforces this (redirects /app/* and 403s /api/*); doing it here too keeps
    // the UX clean so they never flash a half-loaded console. Everyone else
    // lands in the app; the ConsoleShell resolves their landing screen.
    if (typeof window !== "undefined") {
      window.location.assign(json.user?.mustChangePassword ? "/change-password" : "/app");
    }
    return "ok";
  }
  const code = json.error?.error?.code;
  if (code === "ACCOUNT_LOCKED") return "accountLocked";
  if (code === "ORG_SUSPENDED" || code === "USER_SUSPENDED") return "accountSuspended";
  if (code === "EMAIL_UNVERIFIED") return "emailUnverified";
  if (code === "INVALID_CREDENTIALS") return "invalidCredentials";
  // Any 5xx / unexpected status with no recognised credential code is a
  // server-side failure, not a user credential problem.
  if (status >= 500) return "serverError";
  return "invalidCredentials";
}

/** Forgot password → POST /api/auth/forgot-password. Always neutral. */
export async function apiForgotPassword(email: string): Promise<{ ok: boolean; message: string }> {
  const { json } = await postWithStatus<{ ok?: boolean; message?: string }>("/api/auth/forgot-password", { email });
  return { ok: Boolean(json.ok), message: json.message ?? "If an account exists, we sent reset instructions." };
}

/** Reset password → POST /api/auth/reset-password. */
export async function apiResetPassword(token: string, password: string): Promise<{ ok: boolean; error?: string }> {
  const { status, json } = await postWithStatus<{ ok?: boolean; error?: { error?: { message?: string } } }>("/api/auth/reset-password", { token, password });
  if (status === 200 && json.ok) return { ok: true };
  return { ok: false, error: json.error?.error?.message ?? "Could not reset password." };
}

/**
 * Change password → POST /api/auth/change-password. Used by the first-login
 * Create-Password screen: the user supplies their temporary password as
 * `currentPassword` plus the chosen `newPassword`. On 200 the server clears the
 * must-change flag. Returns the server error CODE so the screen can distinguish
 * an incorrect temporary password (INVALID_CURRENT) from a policy failure.
 */
export async function apiChangePassword(
  currentPassword: string,
  newPassword: string,
): Promise<{ ok: boolean; code?: string; error?: string }> {
  const { status, json } = await postWithStatus<{ ok?: boolean; error?: { error?: { code?: string; message?: string } } }>(
    "/api/auth/change-password",
    { currentPassword, newPassword },
  );
  if (status === 200 && json.ok) return { ok: true };
  const code = json.error?.error?.code;
  const error = json.error?.error?.message ?? "Could not update your password.";
  return code === undefined ? { ok: false, error } : { ok: false, code, error };
}

/** Invitation lookup → POST /api/auth/invite/lookup. */
export async function apiInviteLookup(token: string): Promise<{ email: string; role: string; expired: boolean } | null> {
  const { status, json } = await postWithStatus<{ invitation?: { email: string; role: string; expired: boolean } }>("/api/auth/invite/lookup", { token });
  return status === 200 && json.invitation ? json.invitation : null;
}

/** Invitation accept → POST /api/auth/invite/accept. Redirects to /app on success. */
export async function apiInviteAccept(token: string, password: string): Promise<{ ok: boolean; error?: string }> {
  const { status, json } = await postWithStatus<{ ok?: boolean; error?: { error?: { message?: string } } }>("/api/auth/invite/accept", { token, password });
  if (status === 200 && json.ok) {
    if (typeof window !== "undefined") window.location.assign("/app");
    return { ok: true };
  }
  return { ok: false, error: json.error?.error?.message ?? "Could not accept invitation." };
}

/** Email verification → POST /api/verify-email. */
export async function apiVerify(token: string): Promise<VerificationResult> {
  const data = await postJson<{ result: VerificationResult }>(
    "/api/verify-email",
    { token },
  );
  return data.result;
}

/** Request a new verification email → POST /api/request-verification. */
export async function apiRequestNewVerification(): Promise<void> {
  // The demo API resends for the most-recently-registered email held in the
  // session cookie; no body needed.
  await postJson<{ ok: boolean }>("/api/request-verification", {});
}
